(ns client-portal.client.ai-agent.report-history.data
  "The builder's History view: the saved report's versions, newest (the current one)
   first, a preview of a past version rendered over the header's view, and restoring one
   — which the server saves as a new latest version and continues the builder session
   from. A restore refused because someone saved since the session last did (409) is
   retried from the same dialog with `:overwrite`."
  (:require [client-portal.client.ai-agent.chat.data :as chat.d]
            [client-portal.client.ai-agent.data :as data]
            [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.locations.data :as locations.d]
            [client-portal.client.reports.individual.data :as reports.d]
            [shared.core.reactive :as re]
            [shared.core.util :as util]))

(def selected-version-path (conj data/report-history-path :selected-version))
(def version-reports-path (conj data/report-history-path :version-reports))
(def restore-path (conj data/report-history-path :restore))
(def restore-conflict-path (conj data/report-history-path :restore-conflict))
(def held-report-path (conj data/report-history-path :hold-builder-report?))
(def refreshing-path (conj data/report-history-path :refreshing?))

;; A `:reload` adds an argument ahead of the db, so take it from the end.
(defn- versions-path [& args]
  (conj data/report-history-versions-path (str (chat.d/report-public-id (last args)))))

(defn- view-params [db]
  (let [location-id (locations.d/current-selected-location-id db)]
    (-> {:concept-id (concepts.d/current-concept-id db)}
        (cond-> location-id (assoc :location-id location-id))
        (chat.d/with-builder-view db))))

(re/reg-auth0-loader
  :ai-agent/load-report-history
  versions-path
  (fn [db & _]
    (let [concept-id (concepts.d/current-concept-id db)
          report-id (chat.d/report-public-id db)]
      (when (and concept-id report-id)
        {:method :get
         :url (str "/api/reports/i/" report-id "/history")
         :query-params {:concept-id concept-id}})))
  :transf #(some-> % :body (util/->kebab-case-keywords) (vec))
  :error-transf (fn [] {:error? true})
  :on-success (fn [db & _]
                (assoc-in db refreshing-path false))
  :on-fail (fn [db & _]
             (re/dispatch [:toasts/add-error {:msg "Failed to load the report's history" :duration 3000}])
             (assoc-in db refreshing-path false)))

(re/reg-auth0-load-sub
  :ai-agent/report-history-
  versions-path
  :ai-agent/load-report-history)

;; Keeps the loaded versions so the preview and newer-version banner hold while it reloads.
(re/reg-event-db
  :ai-agent/refresh-report-history re/middleware
  (fn [db]
    (if (and (concepts.d/current-concept-id db) (chat.d/report-public-id db))
      (do
        (re/dispatch [:ai-agent/load-report-history :reload])
        (assoc-in db refreshing-path true))
      db)))

(re/reg-sub
  :ai-agent/report-history-refreshing?-
  (fn [db]
    (boolean (get-in db refreshing-path))))

(defn- versions [db]
  (let [loaded (get-in db (versions-path db))]
    (when (vector? loaded)
      loaded)))

(defn newer-version-saved?
  "Someone has saved the report since this builder session last saved it (or opened it),
   so what the builder shows is no longer its current version. Known from the session
   load, or from the history list once that has loaded a newer one."
  [db]
  (let [{:keys [report-content-version report-latest-content-version]} (chat.d/session db)
        latest (->> [report-latest-content-version (-> (versions db) (first) :content-version)]
                    (remove nil?)
                    (apply max 0))]
    (boolean (and report-content-version (> latest report-content-version)))))

(defn editing-version
  "The history version the builder is editing from: the current one, unless a newer one
   has been saved since — then the newest snapshot of the content this session last saw,
   or nil when that snapshot predates content versions being recorded."
  [db]
  (if (newer-version-saved? db)
    (let [base (:report-content-version (chat.d/session db))]
      (some #(when (= (:content-version %) base) (:version %)) (versions db)))
    (-> (versions db) (first) :version)))

(defn- selected-version
  "The picked version, while it still belongs to the report being edited — a save as a
   copy moves the builder onto another report whose versions share the same numbers."
  [db]
  (let [selected (get-in db selected-version-path)]
    (when (= (:report-id selected) (chat.d/report-public-id db))
      selected)))

(defn previewed-version
  "The history row of the version being previewed in place of the builder's own report —
   only while the History view is showing, so switching to Chat or Guide puts it back.
   `:current?` when it is the report's current version (one saved since the builder's)."
  [db]
  (let [{:keys [version]} (selected-version db)
        vs (versions db)]
    (when-let [entry (and version
                          (not= version (editing-version db))
                          (= (get-in db data/builder-panel-view-path) "history")
                          (some #(when (= (:version %) version) %) vs))]
      (assoc entry :current? (= entry (first vs))))))

(re/reg-sub :ai-agent/previewed-report-version- previewed-version)

;; A view change while a version is previewed re-renders the builder's report out of
;; sight, so going back to it before that lands must not show the old render.
(re/reg-event-db
  :ai-agent/builder-view-changed re/middleware
  (fn [db]
    (assoc-in db held-report-path (some? (previewed-version db)))))

(re/reg-sub
  :ai-agent/builder-report-held?-
  (fn [db]
    (boolean (and (get-in db held-report-path)
                  (get-in db chat.d/session-reloading-path)))))
(re/reg-sub :ai-agent/newer-report-version-saved?- newer-version-saved?)
(re/reg-sub :ai-agent/editing-report-version- editing-version)

(re/reg-event-db
  :ai-agent/view-latest-report-version re/middleware
  (fn [db]
    (-> db
        (assoc-in data/builder-panel-view-path "history")
        (assoc-in selected-version-path {:report-id (chat.d/report-public-id db)
                                         :version (-> (versions db) (first) :version)}))))

(re/reg-event-db
  :ai-agent/select-report-version re/middleware
  (fn [db [version]]
    (assoc-in db selected-version-path (when version
                                         {:report-id (chat.d/report-public-id db)
                                          :version version}))))

;; Keyed by the view it renders over, so a header change re-renders the preview too.
(defn- version-report-path [& args]
  (let [db (last args)
        {:keys [report-id version]} (selected-version db)]
    (conj version-reports-path report-id version (hash (view-params db)))))

(re/reg-auth0-loader
  :ai-agent/load-report-version
  version-report-path
  (fn [db & _]
    (let [{:keys [report-id version]} (selected-version db)
          params (view-params db)]
      (when (and report-id version (:concept-id params))
        {:method :post
         :url (str "/api/reports/i/" report-id "/history/" version)
         :json-params params})))
  :transf #(some-> % :body (util/->kebab-case-keywords) (update :configuration util/json-string->map-basic))
  :error-transf (fn [] {:error? true}))

(re/reg-auth0-load-sub
  :ai-agent/report-version-report-
  version-report-path
  :ai-agent/load-report-version)

(re/reg-event-db
  :ai-agent/open-restore-report-version re/middleware
  (fn [db]
    (re/dispatch [:dialog/show [:report/restore-version-dialog]])
    (assoc-in db restore-conflict-path nil)))

(re/reg-event-db
  :ai-agent/close-restore-report-version re/middleware
  (fn [db]
    (re/dispatch [:dialog/hide])
    (assoc-in db restore-conflict-path nil)))

(re/reg-auth0-loader
  :ai-agent/restore-report-version
  restore-path
  (fn [db & args]
    (let [mode (#{:overwrite} (first args))
          {:keys [report-id version]} (selected-version db)
          session-id (chat.d/session-id db)
          params (view-params db)]
      (when (and report-id version session-id (:concept-id params))
        {:method :post
         :url (str "/api/reports/i/" report-id "/history/" version "/restore")
         :json-params (cond-> (assoc params :session-id session-id)
                        mode (assoc :mode (name mode)))})))
  :transf #(some-> % :body (util/->kebab-case-keywords))
  :on-success (fn [db {:keys [body]}]
                (let [{:keys [message content-version]} (util/->kebab-case-keywords body)
                      report-id (chat.d/report-public-id db)
                      filters (-> message :saved-report :configuration (util/json-string->map-basic) (get "filters"))
                      ;; The restored report was rendered over a view stripped of the
                      ;; filters the report hid before it — when that changes, re-render.
                      ;; Likewise when a reload from before the restore is still in flight,
                      ;; or its answer would put the unrestored report back.
                      reload? (or (not= (reports.d/settings-filters db report-id)
                                        (reports.d/report-filters filters))
                                  (get-in db chat.d/session-reloading-path))]
                  (re/dispatch [:dialog/hide])
                  (re/dispatch [:toasts/add-info {:msg (str "Version " (:version (selected-version db)) " restored")
                                                  :duration 3000}])
                  (when reload?
                    (re/dispatch-once [:ai-agent/load-session :reload]))
                  (-> db
                      (assoc-in (conj chat.d/session-path :messages) (conj (chat.d/session-messages db) message))
                      (update-in chat.d/session-path merge {:report-content-version content-version
                                                            :report-latest-content-version content-version})
                      (assoc-in selected-version-path nil)
                      (assoc-in held-report-path false)
                      (assoc-in restore-conflict-path nil)
                      (assoc-in data/save-conflict-path nil)
                      (assoc-in data/report-history-versions-path nil)
                      (assoc-in (conj reports.d/settings-by-id-path report-id :filters) filters)
                      (cond-> reload? (assoc-in chat.d/session-reloading-path true)))))
  :on-fail (fn [db {:keys [status body]}]
             (if (= status 409)
               (do
                 ;; Reloaded, not cleared, so the version being restored stays previewed.
                 (re/dispatch [:ai-agent/load-report-history :reload])
                 (assoc-in db restore-conflict-path (util/->kebab-case-keywords body)))
               (do
                 (re/dispatch [:toasts/add-error {:msg "Failed to restore version" :duration 3000}])
                 db))))

(re/reg-sub
  :ai-agent/restoring-report-version?-
  (fn [db]
    (util/loading? (get-in db restore-path))))

(re/reg-sub
  :ai-agent/restore-report-version-conflict-
  (fn [db]
    (get-in db restore-conflict-path)))
